import React from 'react';

// Shared loading-skeleton primitives. Each mirrors the geometry of a real
// component (CgStatusStrip / CgStatusRail panes, CgEntityHeader, CgRailCard,
// CgUnderlineTabs, chart cards) so pages can compose a loading state that
// keeps the final layout's silhouette instead of a spinner or blank space.
// Palette is slate to match the components they stand in for.

export function CgSkeletonBar({ className = '' }) {
  return <div className={`bg-slate-200 rounded animate-pulse ${className}`} />;
}

// One pane of a status strip / status rail: accent line, micro-label,
// bold value, sub line — same paddings as CgStatusStrip's StatusPane and
// CgRailPane (`px-4 py-3`, label mb-1.5, sub mt-1.5).
export function CgStatusPaneSkeleton() {
  return (
    <div className="relative bg-white px-4 py-3 min-w-0">
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-0.5 bg-slate-200"
      />
      <CgSkeletonBar className="h-2.5 w-20 mb-2" />
      <CgSkeletonBar className="h-6 w-16" />
      <CgSkeletonBar className="h-2.5 w-24 mt-2" />
    </div>
  );
}

const STRIP_COLS = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-4',
};

// Loading stand-in for CgStatusStrip / CgStatusRail: the same white divided
// card with `columns` pulsing panes.
export function CgStatusStripSkeleton({ columns = 3, mbClass = 'mb-3', className = '' }) {
  const count = Math.min(Math.max(columns, 1), 4);
  return (
    <div
      className={`bg-white border border-slate-200 rounded-xl overflow-hidden ${mbClass} ${className}`}
      aria-hidden="true"
    >
      <div className={`grid ${STRIP_COLS[count]} divide-x divide-slate-100`}>
        {Array.from({ length: count }).map((_, i) => (
          <CgStatusPaneSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

// Loading stand-in for CgEntityHeader: icon block, title + pill, meta line,
// and right-aligned action buttons.
export function CgEntityHeaderSkeleton({ actions = 2 }) {
  return (
    <div
      className="flex items-start justify-between gap-4 mb-4 flex-wrap"
      aria-hidden="true"
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-11 h-11 rounded-xl bg-slate-200 animate-pulse shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <CgSkeletonBar className="h-5 w-56" />
            <div className="h-4 w-16 rounded-full bg-slate-200 animate-pulse" />
          </div>
          <div className="flex items-center gap-4 mt-2">
            <CgSkeletonBar className="h-3 w-32" />
            <CgSkeletonBar className="h-3 w-20" />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {Array.from({ length: actions }).map((_, i) => (
          <div
            key={i}
            className="h-[30px] w-24 rounded-xl bg-slate-200 animate-pulse"
          />
        ))}
      </div>
    </div>
  );
}

// Loading stand-in for CgRailCard: white card, micro-label, `fields`
// label/value pairs.
export function CgRailCardSkeleton({ fields = 3 }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl px-4 py-3"
      aria-hidden="true"
    >
      <CgSkeletonBar className="h-2.5 w-20 mb-3" />
      {Array.from({ length: fields }).map((_, i) => (
        <div
          key={i}
          className="mb-2 last:mb-0"
        >
          <CgSkeletonBar className="h-2.5 w-14 mb-1" />
          <CgSkeletonBar className="h-3 w-32" />
        </div>
      ))}
    </div>
  );
}

// Loading stand-in for CgUnderlineTabs: bottom-bordered strip of tab bars.
export function CgUnderlineTabsSkeleton({ tabs = 2 }) {
  return (
    <div
      className="flex items-center gap-0.5 border-b border-slate-200 mb-3"
      aria-hidden="true"
    >
      {Array.from({ length: tabs }).map((_, i) => (
        <div
          key={i}
          className="px-3 py-2"
        >
          <CgSkeletonBar className="h-4 w-20" />
        </div>
      ))}
    </div>
  );
}

// Loading stand-in for a chart card body: optional title bar over a soft
// block the height of the eventual canvas.
export function CgChartSkeleton({ height = 240, title = true }) {
  return (
    <div aria-hidden="true">
      {title && <CgSkeletonBar className="h-4 w-40 mb-3" />}
      <div
        className="bg-slate-100 rounded-md animate-pulse w-full"
        style={{ height }}
      />
    </div>
  );
}
